// ============================================================
// txn-utils.js — shared helpers for transaction views
// ============================================================

/**
 * Collapse two-legged transfer rows into a single visual entry.
 * Input: flat array of transactions, sorted by occurred_on DESC.
 * Output: same array but each pair is replaced by its debit leg with
 *   a `_pair` field carrying { debit, credit, from_account_id, to_account_id }.
 * Lone halves of a pair (e.g. when search filters one out) pass through with
 * their original `transfer_pair_id` so the UI can flag them visually.
 */
export function groupPairs(rows) {
  const byPair = new Map();
  const singletons = [];
  for (const t of rows) {
    if (!t.transfer_pair_id) { singletons.push(t); continue; }
    const list = byPair.get(t.transfer_pair_id) ?? [];
    list.push(t);
    byPair.set(t.transfer_pair_id, list);
  }

  const collapsed = [];
  for (const [, legs] of byPair) {
    if (legs.length === 1) { collapsed.push(legs[0]); continue; }
    const debit = legs.find(l => l.amount_cents < 0) ?? legs[0];
    const credit = legs.find(l => l.amount_cents > 0) ?? legs[1];
    collapsed.push({
      ...debit,
      _pair: {
        debit, credit,
        from_account_id: debit.account_id,
        to_account_id: credit.account_id,
      },
    });
  }

  const all = [...singletons, ...collapsed];
  all.sort((a, b) => {
    if (a.occurred_on !== b.occurred_on) return a.occurred_on < b.occurred_on ? 1 : -1;
    return (a.created_at ?? '') < (b.created_at ?? '') ? 1 : -1;
  });
  return all;
}
